import React, { useState } from 'react';
import { Edit, Trash2, Eye, MoreVertical, BookOpen, Calendar, User } from 'lucide-react';
import { EBook } from '@brovicms/models';
import { Timestamp } from "firebase/firestore";

function formatDate(timestamp: Date | Timestamp | any) {
  if (!timestamp) return "N/A";
  
  let date: Date;
  
  // Handle different timestamp types
  if (timestamp instanceof Date) {
    date = timestamp;
  } else if (timestamp && typeof timestamp.toDate === 'function') {
    // Firestore Timestamp
    date = timestamp.toDate();
  } else if (timestamp && typeof timestamp.getTime === 'function') {
    // Already a Date object
    date = timestamp;
  } else {
    // Try to convert string or number to Date
    date = new Date(timestamp);
  }
  
  return date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }); // e.g. "10/22/2025, 5:45 PM"
}
interface EBookTableProps {
  ebooks: EBook[];
  onEdit: (ebook: EBook) => void;
  onDelete: (id: string) => void;
  onView: (ebook: EBook) => void;
}

const EBookTable: React.FC<EBookTableProps> = ({ ebooks, onEdit, onDelete, onView }) => {
  const [selectedEbook, setSelectedEbook] = useState<EBook | null>(null);

  const handleDelete = (ebook: EBook) => {
    if (window.confirm(`Are you sure you want to delete "${ebook.title}"?`)) {
      onDelete(ebook.id);
    }
  };

  const getStatusBadge = (ebook: EBook) => {
    if (ebook.isPublished) {
      return <span className="badge badge-success">Published</span>;
    }
    return <span className="badge badge-warning">Draft</span>;
  };

  const getPremiumBadge = (ebook: EBook) => {
    if (ebook.isPremium) {
      return <span className="badge badge-primary">Premium</span>;
    }
    return <span className="badge badge-gray">Free</span>;
  };

  return (
    <div className="card">
      <div className="card-body p-0">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Book</th>
                <th>Author</th>
                <th>Category</th>
                <th>Status</th>
                <th>Type</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ebooks.map((ebook) => (
                <tr key={ebook.id}>
                  <td>
                    <div className="flex items-center">
                      <div className="w-12 h-16 bg-gray-200 rounded-apple flex items-center justify-center mr-4">
                        {ebook.coverImage ? (
                          <img
                            src={ebook.coverImage.url}
                            alt={ebook.title}
                            className="w-full h-full object-cover rounded-apple"
                          />
                        ) : (
                          <BookOpen className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{ebook.title}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {ebook.description}
                        </div>
                        {ebook.isFeatured && (
                          <span className="badge badge-primary mt-1">Featured</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center text-sm text-gray-900">
                      <User className="w-4 h-4 mr-2 text-gray-400" />
                      {ebook.author}
                    </div>
                  </td>
                  <td>
                    <span className="text-sm text-gray-900 capitalize">
                      {ebook.category?.name || 'Uncategorized'}
                    </span>
                  </td>
                  <td>{getStatusBadge(ebook)}</td>
                  <td>{getPremiumBadge(ebook)}</td>
                  <td>
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(ebook.createdAt instanceof Date ? ebook.createdAt : (ebook.createdAt as any)?.toDate?.() || new Date())}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onView(ebook)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onEdit(ebook)}
                        className="p-1 text-gray-400 hover:text-primary-600"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(ebook)}
                        className="p-1 text-gray-400 hover:text-error-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EBookTable;
